/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { useConfirmationDialog } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { useSetupDialog, uiSelectors, uiTypes } from '@opengeoweb/store';
import type { CustomSortableEvent } from './TimeSeriesManager';
import { TimeSeriesManager } from './TimeSeriesManager';
import { timeSeriesActions } from '../../store';
import { ParameterInfoDialogConnect } from './ParameterInfo/ParameterInfoDialogConnect';
import { getPlotState } from '../../store/selectors';
import type {
  MoveParameterPayload,
  UpdateTitlePayload,
  TimeSeriesRootState,
} from '../../store/types';
import { TimeSeriesActionOrigin } from '../../store/types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import type { Parameter, Plot, PlotPreset } from '../TimeSeries/types';
import { TimeSeriesSelectConnect } from '../TimeSeriesSelect/TimeSeriesSelectConnect';

export const DIALOG_TYPE_MANAGER = uiTypes.DialogTypes.TimeSeriesManager;

const origin = TimeSeriesActionOrigin.timeSeriesManager;

const getNextPlotCopyTitle = (
  sourceTitle: string,
  plots: Plot[],
  t: TFunction,
): string => {
  const titleToken = '__PLOT_TITLE__';
  const countToken = '__COPY_NUMBER__';
  const template = t('timeseries-plot-copy-title', {
    title: titleToken,
    count: countToken,
  });
  const titleTokenIndex = template.indexOf(titleToken);
  const countTokenIndex = template.indexOf(countToken);
  const prefix = template.slice(0, titleTokenIndex);
  const numberPrefix = template.slice(
    titleTokenIndex + titleToken.length,
    countTokenIndex,
  );
  const suffix = template.slice(countTokenIndex + countToken.length);
  const copyTitlePattern = new RegExp(
    `^${escapeRegExp(prefix)}(.*?)${escapeRegExp(numberPrefix)}(\\d+)${escapeRegExp(suffix)}$`,
  );
  const getCopyTitleParts = (
    title: string,
  ): { baseTitle: string; copyNumber: number } | undefined => {
    const match = title.match(copyTitlePattern);
    return match
      ? { baseTitle: match[1], copyNumber: Number(match[2]) }
      : undefined;
  };
  const sourceCopy = getCopyTitleParts(sourceTitle);
  const baseTitle = sourceCopy?.baseTitle ?? sourceTitle;
  const highestCopyNumber = plots.reduce((highest, plot) => {
    const plotCopy = getCopyTitleParts(plot.title);
    return plotCopy?.baseTitle === baseTitle
      ? Math.max(highest, plotCopy.copyNumber)
      : highest;
  }, sourceCopy?.copyNumber ?? 0);

  return t('timeseries-plot-copy-title', {
    title: baseTitle,
    count: highestCopyNumber + 1,
  });
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

interface TimeSeriesManagerProps {
  preloadedTimeseriesServices?: TimeSeriesService[];
}
export const TimeSeriesManagerConnect: FC<TimeSeriesManagerProps> = ({
  preloadedTimeseriesServices,
}) => {
  const panelId = useSelector((store: TimeSeriesRootState) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.TimeSeriesManager),
  );
  const { setDialogOrder, dialogOrder, isDialogOpen, onCloseDialog } =
    useSetupDialog(DIALOG_TYPE_MANAGER);
  const { t } = useTimeseriesTranslation();
  const [selectPlotId, setSelectPlotId] = useState('');
  const dispatch = useDispatch();
  const confirmDialog = useConfirmationDialog();

  const plotState: PlotPreset | undefined = useSelector(
    (state: TimeSeriesRootState) => getPlotState(state, panelId),
  );

  React.useEffect(() => {
    if (preloadedTimeseriesServices) {
      dispatch(
        timeSeriesActions.addServices({
          timeSeriesServices: preloadedTimeseriesServices,
        }),
      );
    }
  }, [preloadedTimeseriesServices, dispatch]);

  const showSnackbar = React.useCallback(
    (message: string) => {
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message,
        }),
      );
    },
    [dispatch],
  );

  const deletePlotSnackbar = React.useCallback(
    (plotId: string) => {
      const message = `${t('timeseries-plot-removed', { id: plotId })}`;
      showSnackbar(message);
      dispatch(timeSeriesActions.deletePlot({ panelId, origin, plotId }));
    },
    [dispatch, panelId, showSnackbar, t],
  );

  const deletePlot = (id: string): void => {
    void confirmDialog({
      title: `${t('timeseries-remove')} ${t('timeseries-plot')}`,
      description: `${t('timeseries-remove-confirm', { id })}`,
      confirmLabel: `${t('timeseries-remove')}`,
      cancelLabel: `${t('timeseries-cancel')}`,
    }).then(() => {
      deletePlotSnackbar(id);
    });
  };

  const addPlot = (title: string): void => {
    dispatch(timeSeriesActions.addPlot({ panelId, origin, title }));
  };
  const duplicatePlot = (plotId: string): void => {
    const plot = plotState?.plots.find((item) => item.plotId === plotId);
    if (!plot || !plotState) {
      return;
    }
    dispatch(
      timeSeriesActions.duplicatePlot({
        panelId,
        origin,
        plotId,
        title: getNextPlotCopyTitle(plot.title, plotState.plots, t),
      }),
    );
  };
  const deleteParameter = (parameterId: string): void => {
    dispatch(
      timeSeriesActions.deleteParameter({ panelId, origin, parameterId }),
    );
  };
  const togglePlot = (plotId: string): void => {
    dispatch(timeSeriesActions.togglePlot({ panelId, origin, plotId }));
  };
  const toggleParameter = (parameterId: string): void => {
    dispatch(
      timeSeriesActions.toggleParameter({ panelId, origin, parameterId }),
    );
  };
  const patchParameter = (parameter: Partial<Parameter>): void => {
    dispatch(timeSeriesActions.patchParameter({ panelId, origin, parameter }));
  };
  const movePlot = ({ oldIndex, newIndex }: CustomSortableEvent): void => {
    dispatch(
      timeSeriesActions.movePlot({ panelId, origin, oldIndex, newIndex }),
    );
  };
  const moveParameter = (payload: MoveParameterPayload): void => {
    dispatch(timeSeriesActions.moveParameter({ ...payload, panelId, origin }));
  };
  const updateTitle = (payload: UpdateTitlePayload): void => {
    dispatch(timeSeriesActions.updateTitle({ ...payload, panelId, origin }));
  };
  const addParameter = (parameter: Parameter): void => {
    dispatch(timeSeriesActions.addParameter({ panelId, origin, parameter }));
  };

  return (
    <>
      {!!plotState && (
        <TimeSeriesManager
          addParameter={addParameter}
          addPlot={addPlot}
          deleteParameter={deleteParameter}
          deletePlot={deletePlot}
          duplicatePlot={duplicatePlot}
          isOpen={isDialogOpen}
          moveParameter={moveParameter}
          movePlot={movePlot}
          onClose={onCloseDialog}
          onMouseDown={setDialogOrder}
          order={dialogOrder}
          panelId={panelId}
          patchParameter={patchParameter}
          plotState={plotState}
          selectPlotId={selectPlotId}
          setSelectPlotId={setSelectPlotId}
          toggleParameter={toggleParameter}
          togglePlot={togglePlot}
          updateTitle={updateTitle}
        />
      )}
      <TimeSeriesSelectConnect panelId={panelId} selectPlotId={selectPlotId} />
      <ParameterInfoDialogConnect />
    </>
  );
};
